import { UseMutationResult } from "@tanstack/react-query";
import { CheckCircle2, TriangleAlert, XCircle } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@/components/ui/input-group";

import { Coupon, HubPlan } from "data/models";
import { ReactQueryMutationError } from "data/mutations-common";

import {
  ChangeType,
  generateCouponMessage,
  isCouponValid,
} from "./change-summary";

interface CouponCardProps {
  coupon: string;
  setCoupon: (val: string) => void;
  couponMutation: UseMutationResult<
    Coupon,
    ReactQueryMutationError,
    string,
    unknown
  >;
  selectedPlan: HubPlan | null;
  changeType: ChangeType;
}

export const CouponCard = ({
  coupon,
  setCoupon,
  couponMutation,
  selectedPlan,
  changeType,
}: CouponCardProps) => {
  const disabled =
    !selectedPlan || changeType === "downgrade" || changeType === "same";

  const couponValid = isCouponValid(
    selectedPlan?.price_lookup_id ?? "",
    couponMutation.data,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Coupon</CardTitle>
        <CardDescription>Have a coupon code? Enter it here.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        <div className="flex items-end gap-2">
          <Field className="flex-1">
            <FieldLabel htmlFor="coupon-code">Coupon Code</FieldLabel>
            <InputGroup>
              <InputGroupInput
                id="coupon-code"
                disabled={disabled}
                value={coupon}
                onChange={(e) => {
                  couponMutation.reset();
                  setCoupon(e.target.value);
                }}
              />
              {couponMutation.isSuccess && couponValid && (
                <InputGroupAddon align="inline-end">
                  <CheckCircle2 className="text-success" />
                </InputGroupAddon>
              )}
              {(couponMutation.isError ||
                (couponMutation.isSuccess && !couponValid)) && (
                <InputGroupAddon align="inline-end">
                  <XCircle className="text-destructive" />
                </InputGroupAddon>
              )}
            </InputGroup>
          </Field>
          <Button
            disabled={disabled || !coupon || couponMutation.isPending}
            onClick={() => couponMutation.mutate(coupon)}
          >
            Apply
          </Button>
          <Button
            variant="destructive"
            disabled={disabled || !coupon || couponMutation.isPending}
            onClick={() => {
              couponMutation.reset();
              setCoupon("");
            }}
          >
            Cancel
          </Button>
        </div>

        {couponMutation.isSuccess && couponValid && (
          <Alert variant="success">
            <AlertDescription>
              {generateCouponMessage(couponMutation.data)}
            </AlertDescription>
          </Alert>
        )}
        {couponMutation.isError && (
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertDescription>
              {(couponMutation.error as ReactQueryMutationError).message}
            </AlertDescription>
          </Alert>
        )}
        {couponMutation.isSuccess && !couponValid && (
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertDescription>
              Coupon code cannot be applied to the selected subscription
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
